import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, Plus, Search, CheckCircle, XCircle, Clock, RefreshCw,
  Mail, Calendar, MoreVertical, ArrowRight, DollarSign
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const Estimates = () => {
  const { token, loading: authLoading } = useAuth();
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_address: '',
    valid_until: '',
    notes: '',
    terms_and_conditions: '',
    line_items: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 0, amount: 0 }]
  });

  useEffect(() => {
    if (!authLoading && token) {
      fetchEstimates();
    } else if (!authLoading && !token) {
      setLoading(false);
    }
  }, [token, authLoading, statusFilter]);

  const fetchEstimates = async () => {
    try {
      const url = statusFilter === 'all' ? `${API}/estimates` : `${API}/estimates?status=${statusFilter}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEstimates(response.data);
    } catch (error) {
      toast.error('Failed to fetch estimates');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/estimates`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Estimate created');
      setIsDialogOpen(false);
      resetForm();
      fetchEstimates();
    } catch (error) {
      toast.error('Error creating estimate');
    }
  };

  const handleConvertToInvoice = async (estimateId) => {
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 30);
    
    try {
      await axios.post(`${API}/estimates/${estimateId}/convert-to-invoice?due_date=${dueDate.toISOString().split('T')[0]}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Estimate converted to invoice');
      fetchEstimates();
    } catch (error) {
      toast.error('Error converting estimate');
    }
  };

  const handleUpdateStatus = async (estimateId, status) => {
    try {
      await axios.patch(`${API}/estimates/${estimateId}/status?status=${status}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status updated');
      fetchEstimates();
    } catch (error) {
      toast.error('Error updating status');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_name: '',
      customer_email: '',
      customer_address: '',
      valid_until: '',
      notes: '',
      terms_and_conditions: '',
      line_items: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 0, amount: 0 }]
    });
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, { description: '', quantity: 1, unit_price: 0, tax_rate: 0, amount: 0 }]
    });
  };

  const updateLineItem = (index, field, value) => {
    const items = [...formData.line_items];
    items[index][field] = value;
    items[index].amount = items[index].quantity * items[index].unit_price * (1 + items[index].tax_rate / 100);
    setFormData({ ...formData, line_items: items });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted': return 'bg-emerald-500/20 text-emerald-400';
      case 'sent': return 'bg-blue-500/20 text-blue-400';
      case 'declined': return 'bg-red-500/20 text-red-400';
      case 'expired': return 'bg-slate-500/20 text-slate-400';
      case 'converted': return 'bg-purple-500/20 text-purple-400';
      default: return 'bg-amber-500/20 text-amber-400';
    }
  };

  const filteredEstimates = estimates.filter(e => 
    e.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.estimate_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalValue = estimates.reduce((sum, e) => sum + (e.total || 0), 0);
  const acceptedValue = estimates.filter(e => e.status === 'accepted').reduce((sum, e) => sum + e.total, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Estimates & Quotes</h1>
          <p className="text-slate-400">Create quotes and convert to invoices</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-2" />
              New Estimate
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-700 max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Create New Estimate</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Customer Name *</Label>
                  <Input
                    value={formData.customer_name}
                    onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Customer Email *</Label>
                  <Input
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-slate-300">Customer Address</Label>
                  <Input
                    value={formData.customer_address}
                    onChange={(e) => setFormData({...formData, customer_address: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Valid Until *</Label>
                  <Input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white"
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-300">Line Items</Label>
                <div className="space-y-2 mt-2">
                  {formData.line_items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        className="col-span-4 bg-slate-800 border-slate-700 text-white"
                      />
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                        className="col-span-2 bg-slate-800 border-slate-700 text-white"
                      />
                      <Input
                        type="number"
                        placeholder="Price"
                        value={item.unit_price}
                        onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                        className="col-span-2 bg-slate-800 border-slate-700 text-white"
                      />
                      <Input
                        type="number"
                        placeholder="Tax %"
                        value={item.tax_rate}
                        onChange={(e) => updateLineItem(index, 'tax_rate', parseFloat(e.target.value) || 0)}
                        className="col-span-2 bg-slate-800 border-slate-700 text-white"
                      />
                      <div className="col-span-2 text-white text-sm">
                        MYR {item.amount.toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addLineItem} className="mt-2">
                  + Add Line
                </Button>
              </div>

              <div>
                <Label className="text-slate-300">Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-white"
                  rows={2}
                />
              </div>

              <div>
                <Label className="text-slate-300">Terms & Conditions</Label>
                <Textarea
                  value={formData.terms_and_conditions}
                  onChange={(e) => setFormData({...formData, terms_and_conditions: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-white"
                  rows={2}
                />
              </div>

              <div className="flex justify-between items-center pt-4 border-t border-slate-700">
                <div className="text-lg font-semibold text-white">
                  Total: MYR {formData.line_items.reduce((sum, item) => sum + item.amount, 0).toFixed(2)}
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                    Create Estimate
                  </Button>
                </div>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-500/20 rounded-lg">
                <FileText className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Total Estimates</p>
                <p className="text-xl font-bold text-white">{estimates.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <DollarSign className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Total Value</p>
                <p className="text-xl font-bold text-white">MYR {totalValue.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Accepted Value</p>
                <p className="text-xl font-bold text-white">MYR {acceptedValue.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search estimates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-800/50 border-slate-700 text-white"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-slate-800/50 border-slate-700 text-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="all" className="text-white">All Status</SelectItem>
            <SelectItem value="draft" className="text-white">Draft</SelectItem>
            <SelectItem value="sent" className="text-white">Sent</SelectItem>
            <SelectItem value="accepted" className="text-white">Accepted</SelectItem>
            <SelectItem value="declined" className="text-white">Declined</SelectItem>
            <SelectItem value="converted" className="text-white">Converted</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-10">
          <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
        </div>
      ) : filteredEstimates.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-10 text-center">
            <FileText className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <p className="text-slate-400">No estimates found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredEstimates.map((estimate) => (
            <motion.div
              key={estimate.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-indigo-500/20 rounded-xl flex items-center justify-center">
                        <FileText className="w-6 h-6 text-indigo-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-white">{estimate.estimate_number}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(estimate.status)}`}>
                            {estimate.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-400 mt-1">
                          <span>{estimate.customer_name}</span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Valid until: {estimate.valid_until}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-semibold text-white">
                          {estimate.currency} {estimate.total?.toLocaleString()}
                        </p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4 text-slate-400" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-slate-800 border-slate-700">
                          {estimate.status === 'draft' && (
                            <DropdownMenuItem 
                              className="text-slate-300"
                              onClick={() => handleUpdateStatus(estimate.id, 'sent')}
                            >
                              <Mail className="w-4 h-4 mr-2" />
                              Mark as Sent
                            </DropdownMenuItem>
                          )}
                          {estimate.status === 'sent' && (
                            <>
                              <DropdownMenuItem 
                                className="text-emerald-400"
                                onClick={() => handleUpdateStatus(estimate.id, 'accepted')}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Mark Accepted
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-red-400"
                                onClick={() => handleUpdateStatus(estimate.id, 'declined')}
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Mark Declined
                              </DropdownMenuItem>
                            </>
                          )}
                          {(estimate.status === 'accepted' || estimate.status === 'sent') && estimate.status !== 'converted' && (
                            <DropdownMenuItem 
                              className="text-purple-400"
                              onClick={() => handleConvertToInvoice(estimate.id)}
                            >
                              <ArrowRight className="w-4 h-4 mr-2" />
                              Convert to Invoice
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Estimates;
